import axios, { CancelToken } from 'axios';
import { Observable } from 'rxjs/Observable';

import { getDeepProp, isNil, ResponseError } from 'vdlib';
import { SERVER_BASE_URL } from './Env';
import Auth from './AuthHelper';
import Store from 'data/Store';

// Default axios configuration
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.timeout = 120000;

export const REQ_GET = 'get';
export const REQ_POST = 'post';
export const REQ_PUT = 'put';

const cth = 'content-type';
const jsonct = 'application/json';

const constructUrl = (resource, controller) =>
  `${SERVER_BASE_URL}${controller || 'api'}/${resource}`;

// add authentication headers to the request
const configWithAuth = configObj => {
  let headerValue = `Bearer ${Auth.getAuthToken()}`;

  if (isNil(configObj)) configObj = {};

  configObj.headers = Object.assign(configObj.headers || {}, {
    Authorization: headerValue,
  });

  return configObj;
};

// Set the cancellation token to the request config
const configWithCancellation = (configObj, cancellationToken) => {
  let config = configObj || {};
  return Object.assign(config, {
    cancelToken: cancellationToken,
    headers: Object.assign({}, config.headers, {
      Culture: Store.getState().i18n.locale,
    }),
    retryOnTimeout: false,
  });
};

// check if success
const isSuccess = resp => resp.data.Success === true;

// wrap request as observable
const observableRequest = (
  type,
  resource,
  config,
  data,
  { anonymous, json } = {}
) => {
  let source = CancelToken.source(),
    formattedConfig =
      anonymous === true
        ? configWithCancellation(config, source.token)
        : configWithAuth(configWithCancellation(config, source.token)),
    request =
      type === REQ_POST
        ? axios.post(constructUrl(resource), data, formattedConfig)
        : type === REQ_PUT
        ? axios.put(constructUrl(resource), data, formattedConfig)
        : axios.get(constructUrl(resource), formattedConfig);

  if (json === true) {
    request = request.then(resp => {
      if (
        resp.headers &&
        resp.headers[cth] &&
        resp.headers[cth] &&
        resp.headers[cth].match(jsonct)
      ) {
        if (isSuccess(resp)) {
          if (typeof resp.data.Content.AccessToken === 'string') {
            Auth.storeAuthToken(resp.data.Content.AccessToken);
            delete resp.data.Content.AccessToken;
          }
          return resp.data.Content;
        }

        throw new ResponseError(
          getDeepProp(resp, 'data.Message.Description', 'Empty response'),
          resp
        );
      }

      throw new ResponseError('Invalid response format', resp);
    });
  }

  //request[REQ_CANCEL] = () => source.cancel();

  return Observable.fromPromise(request);
};

const observableUpload = (resource, config, file, data) => {
  let formData = new FormData(),
    source = CancelToken.source(),
    formattedConfig = configWithAuth(
      configWithCancellation(config, source.token)
    );
  formattedConfig.headers['content-type'] = 'multipart/form-data';
  if (Array.isArray(file))
    for (let i = 0; i < file.length; i++)
      formData.append('file[]', file[i], file[i].name);
  else formData.append('file[]', file, file.name);

  if (!isNil(data))
    for (let key of Object.keys(data))
      formData.append(key, JSON.stringify(data[key]));

  let request = axios
    .post(constructUrl(resource, 'file'), formData, formattedConfig)
    .then(resp => {
      if (isSuccess(resp)) return resp.data.Content;

      throw new ResponseError(
        getDeepProp(resp, 'data.Message.Description', 'Empty response'),
        resp
      );
    });

  return Observable.fromPromise(request);
};

const RequestHelper = {
  anonymousPost: (resource, data, config) =>
    observableRequest(REQ_POST, resource, config, data, { anonymous: true }),
  anonymousPostJson: (resource, data, config) =>
    observableRequest(REQ_POST, resource, config, data, {
      anonymous: true,
      json: true,
    }),
  constructUrl,
  get: (resource, config) => observableRequest(REQ_GET, resource, config),
  getJson: (resource, config) =>
    observableRequest(REQ_GET, resource, config, null, { json: true }),
  post: (resource, data, config) =>
    observableRequest(REQ_POST, resource, config, data),
  postJson: (resource, data, config) =>
    observableRequest(REQ_POST, resource, config, data, { json: true }),
  put: (resource, data, config) =>
    observableRequest(REQ_PUT, resource, config, data),
  putJson: (resource, data, config) =>
    observableRequest(REQ_PUT, resource, config, data, { json: true }),
  uploadFile: (resource, file, data, config) =>
    observableUpload(resource, config, file, data),
};

export default RequestHelper;
